// T-170 Fase 1d — CRUD sul draft locale `document_drafts` (SQLCipher).
// Persistito PRIMA del primo upload (P4): cattura + pagine sopravvivono a
// kill/background; la ripresa ri-carica solo le pagine `uploaded=false`.
// Mirror del pattern pending_ops (db.run/db.query via openLocalDb).

import { openLocalDb } from './local.js';
import { DRAFT_STATES, type DraftState } from '@trackeat/shared';

export interface DraftPage {
  /** path su fiscal-photo-staging (tenant/uuid.ext) — assegnato all'upload. */
  staging_path: string;
  /** base64 dell'immagine, tenuto finché non uploadata (poi può essere svuotato). */
  b64: string | null;
  mime: string;
  uploaded: boolean;
}

export interface DocumentDraft {
  draft_id: string;
  tenant_id: string;
  business_id: string;
  doc_kind: string | null;
  state: DraftState;
  pages: DraftPage[];
  server_job_id: string | null;
}

interface DraftRow {
  draft_id: string;
  tenant_id: string;
  business_id: string;
  doc_kind: string | null;
  state: string;
  pages: string;
  server_job_id: string | null;
}

function toDraft(row: DraftRow): DocumentDraft {
  const state = (DRAFT_STATES as readonly string[]).includes(row.state)
    ? (row.state as DraftState)
    : 'capturing';
  let pages: DraftPage[] = [];
  try {
    pages = JSON.parse(row.pages) as DraftPage[];
  } catch {
    pages = [];
  }
  return {
    draft_id: row.draft_id,
    tenant_id: row.tenant_id,
    business_id: row.business_id,
    doc_kind: row.doc_kind,
    state,
    pages,
    server_job_id: row.server_job_id,
  };
}

export async function upsertDraft(d: DocumentDraft): Promise<void> {
  const db = await openLocalDb(null);
  await db.run(
    `INSERT INTO document_drafts(draft_id, tenant_id, business_id, doc_kind, state, pages, server_job_id, updated_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, datetime('now'))
     ON CONFLICT(draft_id) DO UPDATE SET
       doc_kind = excluded.doc_kind,
       state = excluded.state,
       pages = excluded.pages,
       server_job_id = excluded.server_job_id,
       updated_at = datetime('now')`,
    [
      d.draft_id, d.tenant_id, d.business_id, d.doc_kind ?? null,
      d.state, JSON.stringify(d.pages), d.server_job_id ?? null,
    ],
  );
}

export async function setDraftState(draftId: string, state: DraftState): Promise<void> {
  const db = await openLocalDb(null);
  await db.run(
    `UPDATE document_drafts SET state = ?, updated_at = datetime('now') WHERE draft_id = ?`,
    [state, draftId],
  );
}

export async function setDraftPages(draftId: string, pages: DraftPage[]): Promise<void> {
  const db = await openLocalDb(null);
  await db.run(
    `UPDATE document_drafts SET pages = ?, updated_at = datetime('now') WHERE draft_id = ?`,
    [JSON.stringify(pages), draftId],
  );
}

export async function setDraftServerJob(draftId: string, jobId: string): Promise<void> {
  const db = await openLocalDb(null);
  await db.run(
    `UPDATE document_drafts SET server_job_id = ?, state = 'enqueued', updated_at = datetime('now') WHERE draft_id = ?`,
    [jobId, draftId],
  );
}

export async function getDraft(draftId: string): Promise<DocumentDraft | null> {
  const db = await openLocalDb(null);
  const res = await db.query(`SELECT * FROM document_drafts WHERE draft_id = ?`, [draftId]);
  const row = (res.values ?? [])[0] as DraftRow | undefined;
  return row ? toDraft(row) : null;
}

/** Draft NON terminali (capturing/uploading/enqueue_pending) — candidati alla ripresa. */
export async function listResumableDrafts(): Promise<DocumentDraft[]> {
  const db = await openLocalDb(null);
  const res = await db.query(
    `SELECT * FROM document_drafts WHERE state IN ('capturing','uploading','enqueue_pending') ORDER BY created_at ASC`,
  );
  return (res.values ?? []).map((r) => toDraft(r as DraftRow));
}

export async function deleteDraft(draftId: string): Promise<void> {
  const db = await openLocalDb(null);
  await db.run(`DELETE FROM document_drafts WHERE draft_id = ?`, [draftId]);
}
